// Sessions stay scoped to each app. Only verified server exchanges cross the
// boundary; token values never enter navigation URLs or cross-origin requests.
export const SHARED_SESSION_KEY = 'arawra_shared_session';
const KEYS = {
  game: { token: 'pixelgame_authToken', user: null, url: '/pixelgame/api/auth/session-exchange' },
  social: { token: 'arawra_social_token', user: 'arawra_social_user', url: '/social/api/auth/session-exchange' },
};

export function createSharedSession(app, { location = window.location, storage = window.localStorage,
  fetcher = window.fetch.bind(window), nonce = () => window.crypto.randomUUID() } = {}) {
  const own = KEYS[app], otherApp = app === 'game' ? 'social' : 'game', other = KEYS[otherApp];
  const enabled = () => ['arawra.net', 'www.arawra.net'].includes(location.hostname)
    && (app !== 'social' || !location.pathname || location.pathname === '/social' || location.pathname.startsWith('/social/'));
  const read = key => { try { return storage.getItem(key); } catch { return null; } };
  const write = (key, value) => { try { if (value == null) storage.removeItem(key); else storage.setItem(key, value); } catch {} };
  const state = () => { try { return JSON.parse(read(SHARED_SESSION_KEY) || 'null'); } catch { return null; } };
  const stampKey = target => 'arawra_shared_' + target + '_source';
  const pending = new Map();
  let lastPublishedToken = null;
  let authRevision = 0;
  async function exchange(target, sourceToken, stamp) {
    const key = target + ':' + stamp;
    if (pending.has(key)) return pending.get(key);
    const targetKeys = KEYS[target];
    const promise = (async () => {
      const response = await fetcher(targetKeys.url, { method: 'POST', cache: 'no-store', credentials: 'omit', redirect: 'error',
        headers: { 'Content-Type': 'application/json' }, signal: AbortSignal.timeout(10000),
        body: JSON.stringify(target === 'game' ? { socialToken: sourceToken } : { gameToken: sourceToken }) });
      const result = await response.json();
      if (!response.ok || !result.token || !result.user || result.mfa_required) return result;
      const current = state();
      if (current?.nonce !== stamp || current.loggedOut) return null;
      write(targetKeys.token, result.token);
      if (targetKeys.user) write(targetKeys.user, JSON.stringify(result.user));
      write(stampKey(target), stamp);
      return result;
    })();
    pending.set(key, promise);
    try { return await promise; } finally { if (pending.get(key) === promise) pending.delete(key); }
  }
  async function restore() {
    if (!enabled()) return null;
    let current = state();
    if (current?.loggedOut) return null;
    if (current && (!KEYS[current.app] || typeof current.nonce !== 'string')) return null;
    if (!current) {
      // Existing independent sessions are retained until the user explicitly
      // signs in/switches. A missing app session may adopt the other app's one.
      if (read(own.token) || !read(other.token)) return null;
      current = { app: otherApp, nonce: nonce() };
      write(SHARED_SESSION_KEY, JSON.stringify(current));
      write(stampKey(otherApp), current.nonce);
    }
    if (current.app === app || read(stampKey(app)) === current.nonce && read(own.token)) return null;
    const sourceToken = read(KEYS[current.app]?.token);
    if (!sourceToken) return null;
    write(own.token, null);
    if (own.user) write(own.user, null);
    return exchange(app, sourceToken, current.nonce);
  }
  function publish(token) {
    if (!enabled() || !token) return Promise.resolve(null);
    if (lastPublishedToken === token && state()?.app === app) return Promise.resolve(null);
    lastPublishedToken = token;
    write(own.token, token);
    const current = { app, nonce: nonce() };
    write(stampKey(app), current.nonce);
    write(SHARED_SESSION_KEY, JSON.stringify(current));
    write(other.token, null);
    if (other.user) write(other.user, null);
    write(stampKey(otherApp), null);
    return exchange(otherApp, token, current.nonce);
  }
  function logout() {
    authRevision++;
    if (!enabled()) return;
    lastPublishedToken = null;
    for (const name of Object.keys(KEYS)) {
      write(KEYS[name].token, null); if (KEYS[name].user) write(KEYS[name].user, null);
      write(stampKey(name), null);
    }
    write(SHARED_SESSION_KEY, JSON.stringify({ app, nonce: nonce(), loggedOut: true }));
  }
  return { enabled, restore, publish, logout, beginAuth: () => ++authRevision,
    captureAuth: () => authRevision, isAuthCurrent: revision => revision === authRevision, readToken: () => read(own.token),
    mayRestore: () => enabled() && !state()?.loggedOut && !!read(other.token) };
}
